import { LegalLayout, Section } from "@/components/LegalLayout";

const EFFECTIVE = "1 January 2026";

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" effective={EFFECTIVE}>
      <p className="text-[15px] leading-relaxed text-foreground/80 mb-8">
        This Privacy Policy explains how BIIPP (“BIIPP”, “we”, “us”) collects, uses and protects
        information when you use the BIIPP service (the “Service”). We’ve written it to be readable. By
        using the Service you agree to this Policy.
      </p>

      <Section heading="1. Information we collect">
        <ul className="list-disc pl-6 space-y-1.5">
          <li><span className="font-medium text-foreground">Account information</span> — your name, email address, organization name and password (stored only as a strong one-way hash).</li>
          <li><span className="font-medium text-foreground">Content you add</span> — the items you track, including names, issuers, notes, due dates and reference numbers. Sensitive reference numbers are encrypted at rest.</li>
          <li><span className="font-medium text-foreground">Usage and log data</span> — basic technical information such as IP address and timestamps, and an audit record of logins and changes to your organization’s records.</li>
          <li><span className="font-medium text-foreground">Cookies</span> — a small number of strictly necessary cookies used to keep you securely signed in.</li>
        </ul>
      </Section>

      <Section heading="2. How we use information">
        <p>We use the information to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>provide, secure and operate the Service;</li>
          <li>send the reminder and notification emails you configure, and essential account emails such as verification and password reset;</li>
          <li>maintain an audit trail and protect against fraud, abuse and unauthorised access;</li>
          <li>respond to your requests and support enquiries; and</li>
          <li>comply with legal obligations.</li>
        </ul>
        <p>We do not sell your personal information, and we do not use your Customer Data for advertising.</p>
      </Section>

      <Section heading="3. Organization isolation">
        <p>
          BIIPP is multi-tenant and strictly isolated. Your organization’s data is accessible only to
          verified members of your organization, according to their role. We do not disclose one
          organization’s data to another.
        </p>
      </Section>

      <Section heading="4. Sharing and sub-processors">
        <p>
          We share information only with service providers who help us run the Service, under obligations
          of confidentiality and only as needed. This includes an email delivery provider used to send the
          Service’s emails. We may also disclose information where required by law, to enforce our terms, or
          to protect the rights, safety and security of BIIPP, our users, or the public.
        </p>
      </Section>

      <Section heading="5. Security">
        <p>
          We apply reasonable technical and organisational measures to protect information, including
          transport encryption, one-way password hashing, encryption of sensitive reference fields at rest,
          role-based access controls, rate limiting and audit logging. No method of transmission or storage
          is completely secure, however, and we cannot guarantee absolute security.
        </p>
      </Section>

      <Section heading="6. Data retention">
        <p>
          We retain Customer Data for as long as your organization’s account is active. An Owner can export
          their data or permanently delete the organization and its associated data from within the Service.
          After deletion, data is removed from our active systems; residual copies may persist briefly in
          routine backups before being overwritten.
        </p>
      </Section>

      <Section heading="7. Your rights">
        <p>
          Depending on where you live, you may have rights to access, correct, export or delete your
          personal information, or to object to or restrict certain processing. You can exercise many of
          these directly in the app (profile settings, data export, and organization deletion) or by
          contacting us. We will respond consistent with applicable law.
        </p>
      </Section>

      <Section heading="8. International transfers">
        <p>
          We may process and store information in countries other than your own. Where we do, we take steps
          to ensure an appropriate level of protection consistent with applicable law.
        </p>
      </Section>

      <Section heading="9. Children">
        <p>The Service is not directed to children and is intended for use by organizations and their adult members.</p>
      </Section>

      <Section heading="10. Changes to this Policy">
        <p>
          We may update this Policy from time to time. The updated version is indicated by a revised
          effective date and becomes effective once posted. Significant changes will be communicated through
          the Service where appropriate.
        </p>
      </Section>

      <Section heading="11. Contact">
        <p>For privacy questions or requests, contact <a className="underline underline-offset-4" href="mailto:privacy@biipp.com">privacy@biipp.com</a>.</p>
      </Section>
    </LegalLayout>
  );
}
